import React, { useState, useEffect } from 'react';
import { getApiKeys, createApiKey, revokeApiKey } from './api';
import PageHeader from './ui/PageHeader';
import Spinner from './ui/Spinner';
import Alert from './ui/Alert';
import EmptyState from './ui/EmptyState';
import ConfirmDialog from './ui/ConfirmDialog';

/**
 * Personal API key management. Rendered inline on Settings and as the /api-keys route page.
 */
export const ApiKeysPanel = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [pendingRevoke, setPendingRevoke] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    try {
      setLoading(true);
      const data = await getApiKeys();
      setKeys(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const data = await createApiKey(newKeyName.trim());
      setCreatedKey(data.key);
      setNewKeyName('');
      await loadKeys();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    const keyId = pendingRevoke?.id;
    setPendingRevoke(null);
    if (!keyId) return;
    setRevoking(keyId);
    setError(null);
    try {
      await revokeApiKey(keyId);
      setKeys(keys.filter(k => k.id !== keyId));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to revoke API key');
    } finally {
      setRevoking(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <Spinner label="Loading API keys…" />;
  }

  return (
    <div>
      <p className="muted" style={{ margin: '0 0 12px' }}>
        Generate personal API keys to authenticate CLI tools and programmatic API access.
        Use the key as a Bearer token in the <code>Authorization</code> header.
      </p>

      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>

      {/* Created key banner: shown only once after creation */}
      {createdKey && (
        <Alert type="success" onDismiss={() => setCreatedKey(null)}>
          <strong style={{ display: 'block', marginBottom: '8px' }}>
            API key created. Copy it now; it won't be shown again.
          </strong>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <code
              className="mono"
              style={{
                flex: 1, padding: '8px 10px', backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)', borderRadius: 'var(--radius)',
                wordBreak: 'break-all', color: 'var(--color-text)',
              }}
            >
              {createdKey}
            </code>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => copyToClipboard(createdKey)}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="small" style={{ margin: '8px 0 0' }}>
            Use it in requests: <code>Authorization: Bearer {createdKey.substring(0, 12)}...</code>
          </p>
        </Alert>
      )}

      <div className="toolbar">
        <input
          type="text"
          className="input grow"
          value={newKeyName}
          onChange={(e) => setNewKeyName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          placeholder="Key name (e.g. gemini-cli, my-script)"
          aria-label="New key name"
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleCreate}
          disabled={creating || !newKeyName.trim()}
        >
          {creating ? 'Creating…' : 'Generate key'}
        </button>
      </div>

      {keys.length > 0 ? (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key preview</th>
                <th>Created</th>
                <th>Last used</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  <td style={{ fontWeight: 500 }}>{k.name}</td>
                  <td><code className="mono">{k.key_preview}</code></td>
                  <td className="muted small">
                    {k.created_at ? new Date(k.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="muted small">
                    {k.last_used ? new Date(k.last_used).toLocaleString() : 'Never'}
                  </td>
                  <td className="actions">
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => setPendingRevoke(k)}
                      disabled={revoking === k.id}
                    >
                      {revoking === k.id ? 'Revoking…' : 'Revoke'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="No API keys yet" message="Generate one above to get started." />
      )}

      <div style={{ marginTop: '16px' }}>
        <p className="small" style={{ margin: '0 0 6px' }}>
          Include your API key in the <code>Authorization</code> header of every request:
        </p>
        <pre
          className="mono"
          style={{
            margin: 0, padding: '12px', overflowX: 'auto', lineHeight: 1.5,
            backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
          }}
        >
{`curl -H "Authorization: Bearer dam_YOUR_KEY_HERE" \\
  https://api.247mediagroup.com/api/v1/domains`}
        </pre>
        <p className="muted small" style={{ margin: '6px 0 0' }}>
          For Gemini CLI / MCP, set the <code>DAM_API_KEY</code> environment variable and reference it in your tool configuration.
        </p>
      </div>

      <ConfirmDialog
        open={!!pendingRevoke}
        title="Revoke API key"
        message={`Revoke "${pendingRevoke?.name || ''}"? Anything using it will stop working. This cannot be undone.`}
        confirmLabel="Revoke"
        danger
        onConfirm={handleRevoke}
        onCancel={() => setPendingRevoke(null)}
      />
    </div>
  );
};

const ApiKeys = () => (
  <div className="page page-narrow">
    <PageHeader title="API keys" subtitle="Personal keys for CLI tools and programmatic access" />
    <ApiKeysPanel />
  </div>
);

export default ApiKeys;
